(() => {
  if (window.__ncBambooAtmosphereLoaded || !window.HTMLCanvasElement) return;
  window.__ncBambooAtmosphereLoaded = true;

  const page = document.body;
  if (!page) return;

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointerQuery = window.matchMedia('(pointer: coarse)');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { alpha: true, desynchronized: true });
  if (!context) return;

  canvas.className = 'nc-bamboo-atmosphere';
  canvas.dataset.ncBambooAtmosphere = 'true';
  canvas.setAttribute('aria-hidden', 'true');
  page.append(canvas);

  const colors = {
    light: [
      [37, 67, 41],
      [73, 99, 59],
      [153, 116, 53]
    ],
    dark: [
      [229, 216, 187],
      [177, 151, 91],
      [119, 155, 115]
    ]
  };
  const interactiveSelector = [
    'a[href]',
    'button:not([disabled])',
    'summary',
    '[role="button"]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '.product-card-wrapper',
    '.collection-card'
  ].join(',');

  let width = 0;
  let height = 0;
  let pixelRatio = 1;
  let animationFrame = 0;
  let previousFrameTime = 0;
  let previousScrollY = window.scrollY;
  let previousScrollTime = performance.now();
  let scrollWind = 0;
  let sectionCheckTime = 0;
  let activeSection = '';
  let darkness = 0;
  let targetDarkness = 0;
  let ambientLeafTarget = 0;
  let userPaused = readStoredPause();
  let interfacePaused = page.classList.contains('overflow-hidden');
  let lastInteractiveTarget = null;
  let lastAccentTime = 0;
  let sectionDarknessCache = new WeakMap();
  let attraction = null;
  let tapCandidate = null;
  let randomState = createSeed(`${window.location.pathname}:${window.innerWidth}x${window.innerHeight}`);
  const constrainedDevice = Boolean(
    navigator.connection?.saveData ||
      (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
      window.Shopify?.designMode
  );
  const leaves = [];
  const pointer = {
    x: -1000,
    y: -1000,
    previousX: -1000,
    previousY: -1000,
    active: false,
    lastMove: 0,
    windX: 0,
    windY: 0
  };
  const attractionSelector = [
    '.button:not(.button-unstyled)',
    '.product-form__submit',
    '.shopify-payment-button__button',
    '[data-nc-hero-divider]',
    '[data-nc-footer-seal]',
    '.nc-testimonials__control'
  ].join(',');
  const diagnostics = {
    frames: 0,
    fps: 0,
    fpsFrames: 0,
    fpsStartedAt: performance.now(),
    pointerBursts: 0,
    focusAccents: 0,
    sectionTransitions: 0,
    resizeCount: 0,
    lastInteraction: 'initial'
  };

  function readStoredPause() {
    try {
      return window.localStorage.getItem('nc-bamboo-motion') === 'paused';
    } catch (_error) {
      return false;
    }
  }

  function storePause(paused) {
    try {
      window.localStorage.setItem('nc-bamboo-motion', paused ? 'paused' : 'playing');
    } catch (_error) {
      // Motion still works when storage is unavailable.
    }
  }

  function createSeed(value) {
    let seed = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      seed ^= value.charCodeAt(index);
      seed = Math.imul(seed, 16777619);
    }
    return seed >>> 0 || 1;
  }

  function random() {
    randomState ^= randomState << 13;
    randomState ^= randomState >>> 17;
    randomState ^= randomState << 5;
    return (randomState >>> 0) / 4294967296;
  }

  function randomBetween(minimum, maximum) {
    return minimum + random() * (maximum - minimum);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function mix(start, end, amount) {
    return start + (end - start) * amount;
  }

  function colorAt(index, alpha) {
    const light = colors.light[index % colors.light.length];
    const dark = colors.dark[index % colors.dark.length];
    const red = Math.round(mix(light[0], dark[0], darkness));
    const green = Math.round(mix(light[1], dark[1], darkness));
    const blue = Math.round(mix(light[2], dark[2], darkness));
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  function baseLeafCount() {
    if (width < 750) return constrainedDevice ? 5 : 7;
    if (width < 1100) return constrainedDevice ? 8 : 11;
    return constrainedDevice ? 13 : 18;
  }

  function gutterWidth() {
    return width * (width < 750 ? 0.18 : 0.14);
  }

  function createLeaf(options = {}) {
    const temporary = Boolean(options.temporary);
    const side = options.side || (random() > 0.5 ? 'right' : 'left');
    const gutter = gutterWidth();
    const depth = randomBetween(0.62, 1.12);
    const leaf = {
      ambient: !temporary,
      side,
      x:
        options.x ??
        (side === 'left'
          ? randomBetween(-24, Math.max(gutter, 38))
          : randomBetween(width - Math.max(gutter, 38), width + 24)),
      y: options.y ?? randomBetween(-height * 0.08, height * 1.04),
      velocityX: options.velocityX ?? randomBetween(-0.16, 0.16),
      velocityY: options.velocityY ?? randomBetween(0.16, 0.38),
      angle: options.angle ?? randomBetween(-Math.PI, Math.PI),
      spin: options.spin ?? randomBetween(-0.007, 0.007),
      size: options.size ?? randomBetween(width < 750 ? 8 : 10, width < 750 ? 17 : 25) * depth,
      depth,
      tone: options.tone ?? Math.floor(randomBetween(0, colors.light.length)),
      opacity: options.opacity ?? randomBetween(0.1, 0.23),
      age: 0,
      life: options.life ?? (temporary ? randomBetween(1900, 3400) : Number.POSITIVE_INFINITY),
      seed: randomBetween(0, Math.PI * 2)
    };
    leaves.push(leaf);
    return leaf;
  }

  function resetAmbientLeaves() {
    for (let index = leaves.length - 1; index >= 0; index -= 1) {
      if (leaves[index].ambient) leaves.splice(index, 1);
    }
    ambientLeafTarget = baseLeafCount();
    for (let index = 0; index < ambientLeafTarget; index += 1) {
      createLeaf({ side: index % 2 === 0 ? 'left' : 'right' });
    }
  }

  function recycleAmbientLeaf(leaf, fromTop = true) {
    const side = random() > 0.5 ? 'right' : 'left';
    const gutter = gutterWidth();
    leaf.side = side;
    leaf.x = side === 'left' ? randomBetween(-20, gutter) : randomBetween(width - gutter, width + 20);
    leaf.y = fromTop ? randomBetween(-70, -12) : randomBetween(0, height);
    leaf.velocityX = randomBetween(-0.15, 0.15);
    leaf.velocityY = randomBetween(0.15, 0.36);
    leaf.angle = randomBetween(-Math.PI, Math.PI);
    leaf.spin = randomBetween(-0.006, 0.006);
    leaf.opacity = randomBetween(0.1, 0.22);
  }

  function emitBurst(x, y, count, reason = 'pointer', direction = 1) {
    const maximumLeaves = ambientLeafTarget + (width < 750 ? 7 : 13);
    const available = Math.max(maximumLeaves - leaves.length, 0);
    const amount = Math.min(count, available);
    for (let index = 0; index < amount; index += 1) {
      const angle = randomBetween(-Math.PI * 0.82, Math.PI * 0.18);
      const speed = randomBetween(0.45, 1.25);
      createLeaf({
        temporary: true,
        x: clamp(x + randomBetween(-12, 12), -20, width + 20),
        y: clamp(y + randomBetween(-10, 10), -20, height + 20),
        velocityX: Math.cos(angle) * speed * direction,
        velocityY: Math.sin(angle) * speed + randomBetween(-0.15, 0.28),
        spin: randomBetween(-0.024, 0.024),
        opacity: randomBetween(0.16, 0.3),
        size: randomBetween(width < 750 ? 8 : 10, width < 750 ? 15 : 21),
        life: randomBetween(1800, 3200)
      });
    }
    diagnostics.lastInteraction = reason;
  }

  function emitSectionSweep(scrollDirection) {
    const fromLeft = scrollDirection >= 0;
    const x = fromLeft ? -10 : width + 10;
    const direction = fromLeft ? 1 : -1;
    const y = height * randomBetween(0.3, 0.74);
    emitBurst(x, y, width < 750 ? 2 : 4, 'section-transition', direction);
    diagnostics.sectionTransitions += 1;
  }

  function gatherAt(x, y, duration = 900) {
    if (reducedMotionQuery.matches || userPaused || interfacePaused) return;
    const targetX = clamp(Number(x) || width / 2, 12, width - 12);
    const targetY = clamp(Number(y) || height / 2, 12, height - 12);
    attraction = {
      x: targetX,
      y: targetY,
      expires: performance.now() + clamp(Number(duration) || 900, 240, 2200)
    };

    const maximumLeaves = ambientLeafTarget + (width < 750 ? 7 : 13);
    const amount = Math.min(width < 750 ? 2 : 3, Math.max(maximumLeaves - leaves.length, 0));
    for (let index = 0; index < amount; index += 1) {
      const leaf = createLeaf({
        temporary: true,
        x: targetX + randomBetween(-110, 110),
        y: targetY + randomBetween(-85, 85),
        velocityX: randomBetween(-0.08, 0.08),
        velocityY: randomBetween(-0.08, 0.08),
        opacity: randomBetween(0.18, 0.29),
        life: clamp(Number(duration) || 900, 240, 2200) + 950
      });
      leaf.attracted = true;
    }
    diagnostics.lastInteraction = 'leaf-gather';
    startAnimation();
  }

  function clearGather() {
    attraction = null;
    leaves.forEach((leaf) => {
      leaf.attracted = false;
    });
  }

  function scatterAt(x, y, count = width < 750 ? 3 : 6, direction = 1) {
    clearGather();
    if (reducedMotionQuery.matches || userPaused || interfacePaused) return;
    emitBurst(x, y, count, 'leaf-scatter', direction);
  }

  function accentElement(element, reason = 'element-accent') {
    if (!(element instanceof Element) || reducedMotionQuery.matches || userPaused || interfacePaused) return;
    const now = performance.now();
    if (now - lastAccentTime < 180) return;
    const bounds = element.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0 || bounds.bottom < 0 || bounds.top > height) return;
    lastAccentTime = now;
    const useRightEdge = bounds.left < width * 0.5;
    const x = useRightEdge ? Math.min(bounds.right, width - 8) : Math.max(bounds.left, 8);
    const y = clamp(bounds.top + Math.min(bounds.height * 0.5, 48), 12, height - 12);
    emitBurst(x, y, width < 750 ? 1 : 2, reason, useRightEdge ? 1 : -1);
    diagnostics.focusAccents += 1;
  }

  function drawLeaf(leaf) {
    const lifeFade = leaf.ambient ? 1 : clamp(1 - leaf.age / leaf.life, 0, 1);
    const pulse = 0.92 + Math.sin(leaf.seed + leaf.age * 0.0014) * 0.08;
    const alpha = leaf.opacity * lifeFade * pulse;
    const size = leaf.size;

    context.save();
    context.translate(leaf.x, leaf.y);
    context.rotate(leaf.angle);
    context.scale(leaf.depth, leaf.depth);
    context.fillStyle = colorAt(leaf.tone, alpha);
    context.beginPath();
    context.moveTo(-size * 0.72, 0);
    context.bezierCurveTo(-size * 0.24, -size * 0.48, size * 0.48, -size * 0.34, size, 0);
    context.bezierCurveTo(size * 0.46, size * 0.33, -size * 0.25, size * 0.45, -size * 0.72, 0);
    context.closePath();
    context.fill();
    context.strokeStyle = colorAt(leaf.tone, alpha * 0.75);
    context.lineWidth = Math.max(0.55, size * 0.045);
    context.beginPath();
    context.moveTo(-size * 0.72, 0);
    context.quadraticCurveTo(size * 0.12, size * 0.03, size, 0);
    context.stroke();
    context.restore();
  }

  function drawBambooStalk(x, side, time, layer = 0) {
    const mobile = width < 750;
    const segmentHeight = mobile ? 94 : 112;
    const segments = Math.ceil(height / segmentHeight) + 2;
    const bendDirection = side === 'left' ? 1 : -1;
    const pointerReach = pointer.active
      ? clamp((pointer.x - x) / Math.max(width * 0.45, 1), -1, 1) * bendDirection
      : 0;
    const sway =
      Math.sin(time * 0.00042 + layer * 1.8 + (side === 'left' ? 0 : Math.PI)) * (mobile ? 4 : 7) +
      scrollWind * 1.4 +
      pointerReach * (mobile ? 2 : 4);
    const bottomX = x + layer * 13 * bendDirection;
    const topX = bottomX + sway * 2.2;
    const opacity = (mobile ? 0.075 : 0.095) + darkness * 0.025 - layer * 0.018;

    context.save();
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = colorAt(layer % 2, opacity);
    context.lineWidth = Math.max(2.6, (mobile ? 4.2 : 6.2) - layer * 1.2);
    context.beginPath();
    context.moveTo(bottomX, height + 18);
    context.bezierCurveTo(
      bottomX + sway * 0.25,
      height * 0.68,
      topX - sway * 0.28,
      height * 0.34,
      topX,
      -24
    );
    context.stroke();

    for (let index = 0; index < segments; index += 1) {
      const y = height + 10 - index * segmentHeight;
      const progress = clamp((height - y) / Math.max(height, 1), 0, 1);
      const nodeX = mix(bottomX, topX, progress) + Math.sin(index * 0.8 + time * 0.00028) * sway * 0.12;
      context.strokeStyle = colorAt(2, opacity * 0.9);
      context.lineWidth = mobile ? 1.4 : 2;
      context.beginPath();
      context.moveTo(nodeX - (mobile ? 4 : 6), y);
      context.lineTo(nodeX + (mobile ? 4 : 6), y);
      context.stroke();

      if (index > 1 && index % 2 === layer % 2) {
        const branchDirection = index % 4 < 2 ? bendDirection : -bendDirection;
        context.strokeStyle = colorAt(0, opacity * 0.7);
        context.lineWidth = mobile ? 0.8 : 1.1;
        context.beginPath();
        context.moveTo(nodeX, y - 1);
        context.quadraticCurveTo(
          nodeX + branchDirection * (mobile ? 13 : 22),
          y - segmentHeight * 0.2,
          nodeX + branchDirection * (mobile ? 20 : 34),
          y - segmentHeight * 0.38
        );
        context.stroke();
      }
    }
    context.restore();
  }

  function drawScene(time, updateMotion = true, delta = 1) {
    context.clearRect(0, 0, width, height);
    darkness += (targetDarkness - darkness) * (updateMotion ? 0.055 : 1);

    drawBambooStalk(width < 750 ? 5 : 18, 'left', time, 0);
    drawBambooStalk(width + (width < 750 ? -5 : -18), 'right', time, 0);
    if (width >= 1100) {
      drawBambooStalk(50, 'left', time, 1);
      drawBambooStalk(width - 50, 'right', time, 1);
    }

    if (updateMotion) {
      scrollWind *= Math.pow(0.9, delta);
      pointer.windX *= Math.pow(0.86, delta);
      pointer.windY *= Math.pow(0.86, delta);
      if (performance.now() - pointer.lastMove > 900) pointer.active = false;
    }

    if (attraction && time > attraction.expires) clearGather();
    const gutter = gutterWidth();
    for (let index = leaves.length - 1; index >= 0; index -= 1) {
      const leaf = leaves[index];
      if (updateMotion) {
        leaf.age += delta * 16.67;
        const localBreeze = Math.sin(time * 0.0007 + leaf.seed + leaf.y * 0.008) * 0.025;
        leaf.velocityX += (localBreeze + pointer.windX * 0.012 + scrollWind * 0.006) * leaf.depth * delta;
        leaf.velocityY += pointer.windY * 0.006 * leaf.depth * delta;
        leaf.velocityX *= Math.pow(0.986, delta);
        leaf.velocityY = mix(leaf.velocityY, leaf.ambient ? 0.26 * leaf.depth : leaf.velocityY, 0.01 * delta);

        if (pointer.active) {
          const deltaX = leaf.x - pointer.x;
          const deltaY = leaf.y - pointer.y;
          const distanceSquared = deltaX * deltaX + deltaY * deltaY;
          const radius = width < 750 ? 92 : 145;
          if (distanceSquared > 1 && distanceSquared < radius * radius) {
            const distance = Math.sqrt(distanceSquared);
            const force = (1 - distance / radius) * 0.12 * leaf.depth * delta;
            leaf.velocityX += (deltaX / distance) * force;
            leaf.velocityY += (deltaY / distance) * force;
            leaf.spin += (deltaX < 0 ? -1 : 1) * 0.00035 * delta;
          }
        }

        if (attraction && leaf.attracted) {
          const attractionX = attraction.x - leaf.x;
          const attractionY = attraction.y - leaf.y;
          const attractionDistance = Math.max(Math.hypot(attractionX, attractionY), 1);
          const attractionForce = Math.min(0.055, attractionDistance * 0.00015) * delta;
          leaf.velocityX += (attractionX / attractionDistance) * attractionForce;
          leaf.velocityY += (attractionY / attractionDistance) * attractionForce;
          leaf.velocityX *= Math.pow(0.94, delta);
          leaf.velocityY *= Math.pow(0.94, delta);
        }

        if (leaf.ambient && leaf.x > gutter && leaf.x < width - gutter) {
          leaf.velocityX += (leaf.side === 'left' ? -0.014 : 0.014) * delta;
        }

        leaf.x += leaf.velocityX * delta;
        leaf.y += (leaf.velocityY + scrollWind * 0.08) * delta;
        leaf.angle += leaf.spin * delta;

        if (leaf.ambient && (leaf.y > height + 70 || leaf.x < -100 || leaf.x > width + 100)) {
          recycleAmbientLeaf(leaf, true);
        } else if (!leaf.ambient && leaf.age >= leaf.life) {
          leaves.splice(index, 1);
          continue;
        }
      }
      drawLeaf(leaf);
    }
  }

  function parseBackgroundColor(value) {
    const values = value.match(/[\d.]+/g);
    if (!values || values.length < 3) return null;
    return {
      red: Number(values[0]),
      green: Number(values[1]),
      blue: Number(values[2]),
      alpha: values.length > 3 ? Number(values[3]) : 1
    };
  }

  function sectionIsDark(element) {
    if (element && sectionDarknessCache.has(element)) return sectionDarknessCache.get(element);
    let current = element;
    for (let depth = 0; current && depth < 7; depth += 1) {
      const color = parseBackgroundColor(window.getComputedStyle(current).backgroundColor);
      if (color && color.alpha > 0.25) {
        const luminance = (color.red * 0.2126 + color.green * 0.7152 + color.blue * 0.0722) / 255;
        const isDark = luminance < 0.43;
        if (element) sectionDarknessCache.set(element, isDark);
        return isDark;
      }
      current = current.parentElement;
    }
    if (element) sectionDarknessCache.set(element, false);
    return false;
  }

  function updateActiveSection(scrollDirection = 1) {
    const elements = document.elementsFromPoint(width * 0.5, height * 0.52);
    const section = elements
      .map((element) => element.closest?.('#MainContent > .shopify-section, #MainContent > [id^="shopify-section"], .section-footer'))
      .find(Boolean);
    const nextSection = section?.id || section?.className || 'page';
    targetDarkness = sectionIsDark(section || document.querySelector('#MainContent') || page) ? 1 : 0;
    if (activeSection && nextSection !== activeSection) emitSectionSweep(scrollDirection);
    activeSection = nextSection;
  }

  function animationLoop(time) {
    animationFrame = 0;
    if (userPaused || interfacePaused || reducedMotionQuery.matches || document.hidden) return;

    if (!previousFrameTime) previousFrameTime = time;
    const elapsed = time - previousFrameTime;
    if (elapsed < 31) {
      animationFrame = window.requestAnimationFrame(animationLoop);
      return;
    }

    const delta = clamp(elapsed / 16.67, 0.5, 2.4);
    previousFrameTime = time;
    if (time - sectionCheckTime > 180) {
      updateActiveSection(scrollWind >= 0 ? 1 : -1);
      sectionCheckTime = time;
    }
    drawScene(time, true, delta);
    diagnostics.frames += 1;
    diagnostics.fpsFrames += 1;
    if (time - diagnostics.fpsStartedAt >= 1000) {
      diagnostics.fps = Math.round((diagnostics.fpsFrames * 1000) / (time - diagnostics.fpsStartedAt));
      diagnostics.fpsFrames = 0;
      diagnostics.fpsStartedAt = time;
    }
    animationFrame = window.requestAnimationFrame(animationLoop);
  }

  function startAnimation() {
    if (animationFrame || userPaused || interfacePaused || reducedMotionQuery.matches || document.hidden) return;
    previousFrameTime = 0;
    animationFrame = window.requestAnimationFrame(animationLoop);
    updateControl();
  }

  function stopAnimation(renderStatic = true) {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    if (renderStatic) drawScene(performance.now(), false, 0);
    updateControl();
  }

  function setPaused(paused, persist = true) {
    userPaused = Boolean(paused);
    page.classList.toggle('nc-bamboo-motion-paused', userPaused);
    if (persist) storePause(userPaused);
    if (userPaused) stopAnimation(true);
    else startAnimation();
    updateControl();
  }

  const control = document.createElement('button');
  control.className = 'nc-bamboo-motion-toggle';
  control.type = 'button';
  control.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M19.7 3.7C12.2 4 7.2 6.8 5.2 12.1c-1 2.6-.7 5.1.7 7.1M5.5 17.6c2.7-4.1 6-6.7 10.1-8.2M8.6 13.6c1.7.1 3 .7 4 1.9M12.8 9.5c.2 1.5.9 2.7 2 3.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.55"/>
    </svg>
    <span class="visually-hidden" data-nc-bamboo-control-label></span>
  `;
  control.addEventListener('click', () => setPaused(!userPaused));
  page.append(control);

  function updateControl() {
    const paused = userPaused || reducedMotionQuery.matches;
    const label = paused ? 'Play bamboo motion' : 'Pause bamboo motion';
    control.hidden = reducedMotionQuery.matches;
    control.disabled = interfacePaused;
    control.tabIndex = interfacePaused ? -1 : 0;
    control.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
    control.setAttribute('aria-label', label);
    control.title = label;
    const labelElement = control.querySelector('[data-nc-bamboo-control-label]');
    if (labelElement) labelElement.textContent = label;
    canvas.dataset.motion = reducedMotionQuery.matches ? 'reduced' : userPaused ? 'paused' : 'playing';
  }

  function resizeCanvas() {
    const nextWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const nextHeight = Math.max(window.innerHeight || 0, 1);
    const nextPixelRatio = Math.min(
      window.devicePixelRatio || 1,
      constrainedDevice ? 1 : coarsePointerQuery.matches ? 1.25 : 1.5
    );
    if (nextWidth === width && nextHeight === height && nextPixelRatio === pixelRatio) return;
    width = nextWidth;
    height = nextHeight;
    pixelRatio = nextPixelRatio;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    resetAmbientLeaves();
    updateActiveSection();
    diagnostics.resizeCount += 1;
    if (userPaused || reducedMotionQuery.matches) drawScene(performance.now(), false, 0);
  }

  function handleScroll() {
    const now = performance.now();
    const currentScrollY = window.scrollY;
    const elapsed = Math.max(now - previousScrollTime, 16);
    const delta = currentScrollY - previousScrollY;
    const velocity = (delta / elapsed) * 16.67;
    scrollWind = clamp(scrollWind + velocity * 0.12, -6, 6);
    previousScrollY = currentScrollY;
    previousScrollTime = now;
  }

  function handlePointerMove(event) {
    if (tapCandidate?.pointerId === event.pointerId) {
      const distance = Math.hypot(event.clientX - tapCandidate.x, event.clientY - tapCandidate.y);
      if (distance > 10) tapCandidate = null;
    }
    if (coarsePointerQuery.matches || reducedMotionQuery.matches || userPaused || interfacePaused) return;
    const now = performance.now();
    pointer.previousX = pointer.x;
    pointer.previousY = pointer.y;
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    pointer.windX = clamp((pointer.x - pointer.previousX) * 0.08, -3, 3);
    pointer.windY = clamp((pointer.y - pointer.previousY) * 0.06, -2, 2);
    pointer.active = true;
    pointer.lastMove = now;
  }

  function handlePointerDown(event) {
    if (reducedMotionQuery.matches || userPaused || interfacePaused || event.target === control) return;
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    pointer.active = true;
    pointer.lastMove = performance.now();
    const target = event.target instanceof Element ? event.target.closest(interactiveSelector) : null;
    tapCandidate = target
      ? { pointerId: event.pointerId, x: event.clientX, y: event.clientY, startedAt: performance.now(), target }
      : null;
  }

  function handlePointerUp(event) {
    if (!tapCandidate || tapCandidate.pointerId !== event.pointerId) return;
    const candidate = tapCandidate;
    tapCandidate = null;
    const distance = Math.hypot(event.clientX - candidate.x, event.clientY - candidate.y);
    if (distance > 10 || performance.now() - candidate.startedAt > 800) return;
    scatterAt(event.clientX, event.clientY, width < 750 ? 2 : 4, event.clientX < width / 2 ? 1 : -1);
    diagnostics.pointerBursts += 1;
  }

  function handleInteractiveEntry(event) {
    const target = event.target instanceof Element ? event.target.closest(interactiveSelector) : null;
    if (!target || target === control || target === lastInteractiveTarget) return;
    lastInteractiveTarget = target;
    accentElement(target, event.type === 'focusin' ? 'focus-accent' : 'hover-accent');
    if (target.matches(attractionSelector)) {
      const bounds = target.getBoundingClientRect();
      gatherAt(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2, 1200);
    }
  }

  function handleInteractiveExit(event) {
    const target = event.target instanceof Element ? event.target.closest(interactiveSelector) : null;
    if (!target) return;
    const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (relatedTarget && target.contains(relatedTarget)) return;
    if (lastInteractiveTarget === target) lastInteractiveTarget = null;
    if (target.matches(attractionSelector)) clearGather();
  }

  function handleReducedMotionChange() {
    updateControl();
    if (reducedMotionQuery.matches) stopAnimation(true);
    else if (!userPaused) startAnimation();
  }

  function refreshAfterThemeEdit() {
    sectionDarknessCache = new WeakMap();
    updateActiveSection();
    if (userPaused || reducedMotionQuery.matches) drawScene(performance.now(), false, 0);
  }

  let resizeFrame = 0;
  window.addEventListener(
    'resize',
    () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        resizeCanvas();
      });
    },
    { passive: true }
  );
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('pointermove', handlePointerMove, { passive: true });
  window.addEventListener('pointerdown', handlePointerDown, { passive: true });
  window.addEventListener('pointerup', handlePointerUp, { passive: true });
  window.addEventListener('pointercancel', () => {
    tapCandidate = null;
    clearGather();
  }, { passive: true });
  document.addEventListener('pointerover', handleInteractiveEntry, { passive: true });
  document.addEventListener('pointerout', handleInteractiveExit, { passive: true });
  document.addEventListener('focusin', handleInteractiveEntry);
  document.addEventListener('focusout', handleInteractiveExit);
  document.addEventListener('nc:bamboo-gather', (event) => {
    gatherAt(event.detail?.x, event.detail?.y, event.detail?.duration);
  });
  document.addEventListener('nc:bamboo-scatter', (event) => {
    scatterAt(event.detail?.x, event.detail?.y, event.detail?.count, event.detail?.direction);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopAnimation(false);
    else if (!userPaused && !interfacePaused && !reducedMotionQuery.matches) startAnimation();
  });
  document.addEventListener('shopify:section:load', refreshAfterThemeEdit);
  document.addEventListener('shopify:section:unload', refreshAfterThemeEdit);
  document.addEventListener('shopify:section:reorder', refreshAfterThemeEdit);
  if (reducedMotionQuery.addEventListener) reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
  else reducedMotionQuery.addListener?.(handleReducedMotionChange);

  const interfaceObserver = new MutationObserver(() => {
    const nextInterfacePaused = page.classList.contains('overflow-hidden');
    if (nextInterfacePaused === interfacePaused) return;
    interfacePaused = nextInterfacePaused;
    if (interfacePaused) stopAnimation(false);
    else if (!userPaused && !reducedMotionQuery.matches) startAnimation();
    updateControl();
  });
  interfaceObserver.observe(page, { attributes: true, attributeFilter: ['class'] });

  window.NeuChiefBambooAtmosphere = Object.freeze({
    pause: () => setPaused(true),
    play: () => setPaused(false),
    toggle: () => setPaused(!userPaused),
    gatherAt,
    clearGather,
    scatterAt,
    getDiagnostics: () => ({
      ...diagnostics,
      activeSection,
      ambientLeaves: leaves.filter((leaf) => leaf.ambient).length,
      temporaryLeaves: leaves.filter((leaf) => !leaf.ambient).length,
      totalLeaves: leaves.length,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      cssWidth: width,
      cssHeight: height,
      pixelRatio,
      reducedMotion: reducedMotionQuery.matches,
      paused: userPaused,
      interfacePaused,
      constrainedDevice,
      running:
        Boolean(animationFrame) &&
        !userPaused &&
        !interfacePaused &&
        !reducedMotionQuery.matches &&
        !document.hidden,
      pointerEvents: window.getComputedStyle(canvas).pointerEvents
    })
  });

  resizeCanvas();
  page.classList.add('nc-bamboo-atmosphere-ready');
  page.classList.toggle('nc-bamboo-motion-paused', userPaused);
  updateControl();
  if (reducedMotionQuery.matches || userPaused) stopAnimation(true);
  else startAnimation();
})();
